import { createFileRoute } from '@tanstack/react-router'
import { useServerFn } from "@tanstack/react-start";
import { Mail, MapPin, Instagram, Facebook, Youtube, ArrowRight, MessageCircle } from "lucide-react";
import { useState, type FormEvent } from "react";

import { carregarPaginaSimples } from "@/lib/pagina-simples";
import { enviarContatoLp, getAvaliacoesLp } from "@/lib/lp.functions";
import { MAPS_EMBED, MAPS_BUSCA } from "@/lib/itv/site";
import { useAgendamentoLink } from "@/lib/itv/agendamento";

const DORES = [
  {
    titulo: "Está com sobrepeso ou obesidade:",
    texto: "Sabemos que a obesidade é uma doença e é fator de risco para diversas outras doenças.",
  },
  {
    titulo: "Baixo percentual de massa muscular (sarcopenia):",
    texto:
      "Massa muscular não é somente estética, mas influência diretamente na qualidade da sua saúde.",
  },
  {
    titulo: "Apresenta Baixa libido:",
    texto:
      "Sabemos que a libido (desejo sexual) é algo muito importante em nossa vida e a falta dela gera grandes prejuízos no nosso relacionamento.",
  },
  {
    titulo: "Está com Baixa Disposição:",
    texto:
      "Você que anda muito cansado, está diminuindo sua performance no trabalho, não consegue se concentrar, que anda sentindo que a memória não é mais a mesma.",
  },
  {
    titulo: "Deseja melhorar a qualidade de vida:",
    texto:
      "Trabalharemos juntos para implementar mudanças positivas que promovam uma melhor qualidade de vida.",
  },
];

const TRATAMENTOS = [
  { nome: "Emagrecimento", icone: "/imagens/lp/obesidade-1.png" },
  { nome: "Auxílio no Ganho de Massa Muscular", icone: "/imagens/lp/velho-4.png" },
  { nome: "Melhora da Qualidade de Vida", icone: "/imagens/lp/coracao.png" },
];

const TRATAMENTOS_2 = [
  { nome: "Avaliação Morfológica (Bioimpedância)", icone: "/imagens/lp/balanca.png" },
  { nome: "Atendimento via Teleconsulta", icone: "/imagens/lp/pc.png" },
];

const CREDENCIAIS = [
  "Médico formado pela Universidade Federal de São Paulo - Escola Paulista de Medicina (UNIFESP-EPM) em 2010 - CRM-SP 144.585;",
  "Título de Especialista em Nutrologia RQE 121770;",
  "Título de Especialista em Medicina do Esporte RQE 118488;",
  "Título de Especialista em Ortopedia e Traumatologia RQE: 113822;",
  "Certificação SBMEE em Atendimento Pré-Hospitar de Urgências e emergência em eventos esportivos. (PHSLS - Prehospital Sports Life Support)",
  "Membro da Sociedade Brasileira de Medicina do Exercício e do Esporte (SBMEE)",
  "Membro da Sociedade Brasileira de Medicina da Obesidade - SBEMO:",
  "Membro da European Board of Obesity Medicine – EBOM",
  "Membro da Pan American Board of Obesity Medicine - PABOM",
  "Membro da Sociedade Brasileira de Ortopedia e Traumatologia (SBOT), e de Cirurgia de Ombro e Cotovelo (SBCOC);",
];

const VIDEOS = ["cAjf9-Nu05k", "0ofALMlSIlw", "KB4Hj7fp_ZY"];

const FAQ = [
  {
    pergunta: "É possível emagrecer de forma rápida?",
    resposta:
      "Em alguns casos, a perda de peso pode ser mais rápida no início, mas a velocidade ideal e a segurança do processo variam muito de pessoa para pessoa. O mais importante é que o plano seja individualizado, acompanhado por médico e sustentável a longo prazo.",
  },
  {
    pergunta: "Consigo ganhar massa magra após os 40 anos?",
    resposta:
      "É possível ganhar massa magra após os 40 anos. Existem várias estratégias que podem ajudar nesse processo. O primeiro passo é entender melhor o seu caso e suas necessidades específicas através do acompanhamento médico.",
  },
  {
    pergunta: "Falta de Disposição e Libido pode ser hormonal?",
    resposta:
      "Sim, a falta de disposição e libido reduzida podem ser causadas por desequilíbrios hormonais. No entanto, é importante ressaltar que várias outras condições e fatores podem contribuir para esses sintomas. Para determinar se a causa é hormonal ou não, é fundamental passar por uma avaliação médica completa.",
  },
  {
    pergunta: "Como se livrar do efeito sanfona?",
    resposta:
      "O efeito sanfona é um dos grandes vilões do tratamento da perda de peso, esse efeito é uma condição em que a pessoa perde peso e, em seguida, recupera esse peso perdido de forma cíclica. Na nossa clínica, focamos principalmente no emagrecimento saudável, com ênfase na mudança de hábitos e no ganho de massa muscular, que são dois pilares importantes para prevenir o efeito sanfona.",
  },
];

export const Route = createFileRoute("/lp")({
  loader: async () => {
    const [head, avaliacoes] = await Promise.all([
      carregarPaginaSimples(
        "/lp/",
        "Dr Thiago Viana Médico Especialista em Emagrecimento em Bauru",
        "Emagrecimento saudável, ganho de massa muscular e qualidade de vida com acompanhamento médico próximo e personalizado em Bauru, com o Dr. Thiago Viana.",
      ),
      getAvaliacoesLp(),
    ]);
    return { head, avaliacoes };
  },
  head: ({ loaderData }) => ({
    meta: loaderData?.head?.meta ?? [],
    links: loaderData?.head?.links ?? [],
  }),
  component: PaginaLp,
});

function PaginaLp() {
  const { avaliacoes } = Route.useLoaderData();
  const AGENDAMENTO = useAgendamentoLink();
  return (
    <div className="legado">
      <section className="lp-hero">
        <div className="lp-hero-texto">
          <img
            className="lp-logo"
            src="/imagens/lp/dr_thiago_viana_logomarca2-scaled-1.png"
            alt="ITV Saúde, Bem-Estar e Estética"
            width={2560}
            height={1929}
          />
          <p className="lp-crm">CRM-SP 144.585 – RQE 121770</p>
          <h1>Ajudo você a buscar um emagrecimento saudável para uma vida mais leve e equilibrada.</h1>
          <p className="lp-hero-sub">
            Experimente um cuidado personalizado com tecnologias modernas com foco em saúde à longo
            prazo e qualidade de vida.
          </p>
          <div>
            <a className="lg-btn" href={AGENDAMENTO}>
              Realizar agendamento <ArrowRight size={18} aria-hidden="true" />
            </a>
          </div>
        </div>
        <div className="lp-hero-foto">
          <img src="/imagens/lp/bg-desk.png" alt="Dr. Thiago Viana" width={1920} height={900} />
        </div>
      </section>

      <section className="lp-dores">
        <div className="lg-container lp-dores-grade">
          <div>
            <h2>Nossos tratamentos são para você que:</h2>
            <hr className="lp-regua" />
            {DORES.map((d) => (
              <div className="lp-dor" key={d.titulo}>
                <strong>{d.titulo}</strong>
                <span>{d.texto}</span>
              </div>
            ))}
          </div>
          <div className="lp-dores-foto">
            <img
              src="/imagens/lp/retrato.jpg"
              alt="Dr. Thiago Viana no consultório"
              width={1025}
              height={1536}
              loading="lazy"
            />
          </div>
        </div>
      </section>

      <section className="lp-tratamentos">
        <div className="lg-container">
          <h2 className="lg-titulo">Tratamentos</h2>
          <div className="lp-cards">
            {TRATAMENTOS.map((t) => (
              <div className="lp-card" key={t.nome}>
                <img src={t.icone} alt="" width={64} height={64} loading="lazy" />
                <p>{t.nome}</p>
              </div>
            ))}
          </div>
          <div className="lp-cards-2">
            {TRATAMENTOS_2.map((t) => (
              <div className="lp-card" key={t.nome}>
                <img src={t.icone} alt="" width={64} height={64} loading="lazy" />
                <p>{t.nome}</p>
              </div>
            ))}
          </div>
          <p className="lp-compromisso">
            <strong>Nosso compromisso é</strong> oferecer um acompanhamento próximo e personalizado,
            com contato direto com o Dr. Thiago A. O. F. Viana, em todas as etapas.
          </p>
          <div className="lp-centro">
            <a className="lg-btn" href={AGENDAMENTO}>
              Agendar consulta <ArrowRight size={18} aria-hidden="true" />
            </a>
          </div>
        </div>
      </section>

      <section className="lp-medico">
        <div className="lg-container lp-medico-grade">
          <div className="lp-medico-foto">
            <img
              src="/imagens/lp/hero.jpg"
              alt="Dr. Thiago A. O. F. Viana"
              width={1366}
              height={2048}
              loading="lazy"
            />
          </div>
          <div>
            <h2>Dr. Thiago A. O. F. Viana</h2>
            <p>
              Médico com mais de 15 anos de experiência, formado pela renomada Universidade Federal
              de São Paulo – UNIFESP.
            </p>
            <p>
              Especialista em <strong>Medicina Esportiva e Ortopedia</strong>, com foco no{" "}
              <strong>emagrecimento e qualidade de vida</strong>, especializado em Nutrologia pelo
              Hospital Israelita Albert Einstein, membro da Sociedade Brasileira de Medicina da
              Obesidade e da European Board of Obesity Medicine.
            </p>
            <ul className="lp-lista">
              {CREDENCIAIS.map((c) => (
                <li key={c}>
                  <span className="lp-check" aria-hidden="true">
                    ✓
                  </span>
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="lp-contato" id="contato">
        <div className="lg-container">
          <h2 className="lg-titulo">Contato</h2>
          <div className="lp-contato-grade">
            <div>
              <ul className="lp-info">
                <li>
                  <Mail size={26} aria-hidden="true" />
                  <a href="mailto:consultorio@drthiagoviana.com">consultorio@drthiagoviana.com</a>
                </li>
                <li>
                  <MessageCircle size={26} aria-hidden="true" />
                  <a href={AGENDAMENTO}>
                    (14) 99822-0707
                  </a>
                </li>
                <li>
                  <MapPin size={26} aria-hidden="true" />
                  <a href={MAPS_BUSCA}>
                    Av. Getúlio Vargas, 22-25 - Salas 7 - Torre 2 - Jardim Europa, Bauru - SP,
                    17017-383
                  </a>
                </li>
              </ul>
              <div className="lp-mapa">
                <iframe
                  src={MAPS_EMBED}
                  title="Mapa do consultório em Bauru"
                  width={600}
                  height={450}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            </div>
            <FormularioContato />
          </div>
        </div>
      </section>

      <section className="lp-videos">
        <div className="lg-container">
          <h2 className="lg-titulo">Vídeos do YouTube</h2>
          <div className="lp-videos-grade">
            {VIDEOS.map((id) => (
              <div className="lp-video" key={id}>
                <iframe
                  src={`https://www.youtube-nocookie.com/embed/${id}`}
                  title={`Vídeo do Dr. Thiago Viana no YouTube (${id})`}
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {avaliacoes.length ? (
        <section className="lp-depoimentos">
          <div className="lg-container">
            <h2 className="lg-titulo">Depoimentos</h2>
            <div className="lp-depoimentos-grade">
              {avaliacoes.map((avaliacao) => (
                <figure className="lp-depoimento" key={`${avaliacao.autor}-${avaliacao.texto.slice(0, 24)}`}>
                  <figcaption className="lp-depoimento-topo">
                    <span className="lp-depoimento-avatar" aria-hidden="true">
                      {avaliacao.autor.charAt(0)}
                    </span>
                    <span className="lp-depoimento-dados">
                      <strong>{avaliacao.autor}</strong>
                      <span className="lp-depoimento-estrelas" role="img" aria-label={`${avaliacao.nota} de 5 estrelas`}>
                        {"★".repeat(avaliacao.nota)}
                      </span>
                    </span>
                    <svg className="lp-depoimento-google" viewBox="0 0 24 24" width="20" height="20" aria-label="Publicado no Google" role="img">
                      <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.1h6.5c-.1 1.1-.8 2.7-2.4 3.8l3.7 2.9c2.3-2.1 3.7-5.2 3.7-8.6z" />
                      <path fill="#34A853" d="M12 24c3.2 0 6-1.1 7.9-2.9l-3.7-2.9c-1 .7-2.4 1.2-4.2 1.2-3.2 0-5.9-2.1-6.9-5l-3.9 3C3.1 21.3 7.2 24 12 24z" />
                      <path fill="#FBBC05" d="M5.1 14.4c-.3-.7-.4-1.5-.4-2.4s.1-1.7.4-2.4l-3.9-3C.4 8.2 0 10 0 12s.4 3.8 1.2 5.4l3.9-3z" />
                      <path fill="#EA4335" d="M12 4.7c2.3 0 3.8 1 4.7 1.8l3.4-3.3C18 1.2 15.2 0 12 0 7.2 0 3.1 2.7 1.2 6.6l3.9 3c1-2.9 3.7-4.9 6.9-4.9z" />
                    </svg>
                  </figcaption>
                  <blockquote className="lp-depoimento-texto">{avaliacao.texto}</blockquote>
                </figure>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <section className="lp-faq">
        <div className="lg-container">
          <h2 className="lg-titulo">Perguntas frequentes</h2>
          {FAQ.map((f) => (
            <details className="lp-faq-item" key={f.pergunta}>
              <summary>{f.pergunta}</summary>
              <p>{f.resposta}</p>
            </details>
          ))}
        </div>
      </section>

      <footer className="lp-rodape">
        <div className="lp-redes">
          <a
            href="https://www.instagram.com/drthiagoviana/"
            aria-label="Instagram"
          >
            <Instagram size={28} aria-hidden="true" />
          </a>
          <a
            href="https://www.facebook.com/drthiagoviana"
            aria-label="Facebook"
          >
            <Facebook size={28} aria-hidden="true" />
          </a>
          <a
            href="https://www.youtube.com/@drthiagoviana"
            aria-label="YouTube"
          >
            <Youtube size={28} aria-hidden="true" />
          </a>
        </div>
        <p className="lp-copy">
          <strong>DR THIAGO A. O. F. VIANA ©</strong> Todos os Direitos Reservados.
        </p>
      </footer>

      <a
        className="lp-wpp"
        href={AGENDAMENTO}
        aria-label="Falar pelo WhatsApp"
      >
        <img src="/imagens/lp/wpp-1.png" alt="" width={200} height={200} />
      </a>
    </div>
  );
}

/** Máscara de telefone brasileiro: (14) 99822-0707. */
function mascararTelefone(valor: string): string {
  const d = valor.replace(/\D/g, "").slice(0, 11);
  if (d.length === 0) return "";
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

type ErrosFormulario = { nome?: string; telefone?: string; email?: string; assunto?: string };

/** Formulário de contato da /lp/: grava o lead, envia por e-mail e confirma na tela, sem redirecionar. */
function FormularioContato() {
  const enviar = useServerFn(enviarContatoLp);
  const [nome, setNome] = useState("");
  const [telefone, setTelefone] = useState("");
  const [email, setEmail] = useState("");
  const [assunto, setAssunto] = useState("");
  const [mensagem, setMensagem] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [erros, setErros] = useState<ErrosFormulario>({});

  function validar(): ErrosFormulario {
    const falhas: ErrosFormulario = {};
    if (!nome.trim()) falhas.nome = "Informe seu nome.";
    const digitos = telefone.replace(/\D/g, "");
    if (digitos.length < 10) falhas.telefone = "Informe um telefone válido com DDD, ex.: (14) 99999-0000.";
    const emailLimpo = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(emailLimpo)) {
      falhas.email = emailLimpo.includes("@")
        ? "Confira o e-mail: parece que falta algo depois do ponto, ex.: nome@email.com."
        : "Informe um e-mail válido, ex.: nome@email.com.";
    }
    if (!assunto.trim()) falhas.assunto = "Informe o assunto.";
    return falhas;
  }

  function limparErro(campo: keyof ErrosFormulario) {
    setErros((atuais) => {
      if (!atuais[campo]) return atuais;
      const proximos = { ...atuais };
      delete proximos[campo];
      return proximos;
    });
  }

  async function aoEnviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault();
    const falhas = validar();
    if (Object.keys(falhas).length > 0) {
      setErros(falhas);
      setErro("Revise os campos destacados.");
      const primeiro = document.querySelector<HTMLElement>(".lp-form [aria-invalid='true']");
      primeiro?.focus();
      return;
    }
    setEnviando(true);
    setErro(null);
    setErros({});
    try {
      const resultado = await enviar({
        data: { nome, telefone, email, assunto, mensagem: mensagem || undefined },
      });
      if (resultado.ok) {
        setEnviado(true);
      } else {
        setErro(resultado.erro ?? "Não foi possível enviar sua mensagem agora. Tente novamente.");
      }
    } catch (falha) {
      // Validação do servidor (zod) rejeitou algum campo: mostra erro por campo.
      const issues = (falha as { issues?: { path?: unknown[]; message?: string }[] })?.issues;
      if (Array.isArray(issues) && issues.length > 0) {
        const falhas: ErrosFormulario = {};
        for (const issue of issues) {
          const campo = issue.path?.[0];
          if ((campo === "nome" || campo === "telefone" || campo === "email" || campo === "assunto") && issue.message) {
            falhas[campo] = issue.message;
          }
        }
        if (Object.keys(falhas).length > 0) {
          setErros(falhas);
          setErro("Revise os campos destacados.");
          return;
        }
      }
      setErro("Não foi possível enviar sua mensagem agora. Tente novamente.");
    } finally {
      setEnviando(false);
    }
  }

  function limpar() {
    setNome("");
    setTelefone("");
    setEmail("");
    setAssunto("");
    setMensagem("");
    setEnviado(false);
    setErro(null);
    setErros({});
  }

  if (enviado) {
    return (
      <div className="lp-form lp-form-ok" role="status">
        <p className="lp-form-ok-titulo">Mensagem enviada!</p>
        <p>Recebemos seu contato. Em breve responderemos no telefone ou e-mail informado.</p>
        <button type="button" onClick={limpar}>
          Enviar outra mensagem
        </button>
      </div>
    );
  }

  return (
    <form className="lp-form" onSubmit={aoEnviar} noValidate>
      <div className="lp-campo">
        <input
          type="text"
          value={nome}
          onChange={(e) => { setNome(e.target.value); limparErro("nome"); }}
          placeholder="Nome:*"
          aria-label="Nome"
          required
          maxLength={120}
          aria-invalid={erros.nome ? true : undefined}
          aria-describedby={erros.nome ? "lp-erro-nome" : undefined}
        />
        {erros.nome ? <p className="lp-campo-erro" id="lp-erro-nome">{erros.nome}</p> : null}
      </div>
      <div className="lp-campo">
        <input
          type="tel"
          value={telefone}
          onChange={(e) => { setTelefone(mascararTelefone(e.target.value)); limparErro("telefone"); }}
          placeholder="Telefone:*"
          aria-label="Telefone"
          required
          inputMode="tel"
          aria-invalid={erros.telefone ? true : undefined}
          aria-describedby={erros.telefone ? "lp-erro-telefone" : undefined}
        />
        {erros.telefone ? <p className="lp-campo-erro" id="lp-erro-telefone">{erros.telefone}</p> : null}
      </div>
      <div className="lp-campo">
        <input
          type="email"
          value={email}
          onChange={(e) => { setEmail(e.target.value); limparErro("email"); }}
          placeholder="E-mail:*"
          aria-label="E-mail"
          required
          maxLength={255}
          aria-invalid={erros.email ? true : undefined}
          aria-describedby={erros.email ? "lp-erro-email" : undefined}
        />
        {erros.email ? <p className="lp-campo-erro" id="lp-erro-email">{erros.email}</p> : null}
      </div>
      <div className="lp-campo">
        <input
          type="text"
          value={assunto}
          onChange={(e) => { setAssunto(e.target.value); limparErro("assunto"); }}
          placeholder="Assunto:*"
          aria-label="Assunto"
          required
          maxLength={200}
          aria-invalid={erros.assunto ? true : undefined}
          aria-describedby={erros.assunto ? "lp-erro-assunto" : undefined}
        />
        {erros.assunto ? <p className="lp-campo-erro" id="lp-erro-assunto">{erros.assunto}</p> : null}
      </div>
      <textarea
        value={mensagem}
        onChange={(e) => setMensagem(e.target.value)}
        placeholder="Escreva aqui sua mensagem:"
        aria-label="Mensagem"
        maxLength={2000}
      />
      <button type="submit" disabled={enviando}>
        {enviando ? "Enviando..." : "Enviar"}
      </button>
      {erro ? (
        <p className="lp-form-erro" role="alert">
          {erro}
        </p>
      ) : null}
    </form>
  );
}
